import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNotifications, useProjectMembers } from '@d-orca/shared/client/hooks';
import { memberName } from '@d-orca/shared/client/utils';
import type { INotificationItem } from '@interfaces/INotificationItem';
import type { CommsUnreadNotification, Notification } from '@interfaces/INotificationsApi';
import { selectSiteId } from '@store/selectors/site.selectors';
import { commsUnreadItem, type ICommsUnreadWording } from '../items/commsUnreadItem';
import { useAlertItems } from './useAlertItems';

const NO_NOTIFICATIONS: Notification[] = [];

/**
 * What the header bell shows: an item for each notification waiting for the
 * signed-in person in the working site, and how many there are — each chat
 * item, and each alert not yet viewed. Each kind is handed to its own
 * builder, worded here — this is where the language is. Chat and alerts are
 * one list, newest first. Keeping both current is the bell's: it holds the
 * stream.
 *
 * `open` says the menu is on screen. A direct message is worded after the
 * other person, and names live in the site's member list — so the list is
 * loaded only then, and only when there is one to name. Until a name is known
 * the item says what it is: a direct message.
 */
export function useNotificationItems({ open }: { open: boolean }): {
  count: number;
  items: INotificationItem[];
} {
  const { t } = useTranslation();
  const siteId = useSelector(selectSiteId) ?? null;
  const notifications = useNotifications(siteId).data?.notifications ?? NO_NOTIFICATIONS;

  const hasDirectMessage = notifications.some((n) => n.conversation.kind === 'dm');
  const members = useProjectMembers(open && hasDirectMessage ? siteId : null).data?.members;

  const wordCommsUnread = ({ conversation, unreadCount: count, hasMore }: CommsUnreadNotification): ICommsUnreadWording => {
    // Counting stops at ten: past it the item reads "10+".
    const shown = hasMore ? `${count}+` : String(count);
    if (conversation.kind === 'channel') {
      const { name } = conversation;
      return { text: t('notifications.messagesInChannel', { count, shown, name }), name };
    }
    const peer = members?.find((member) => member.id === conversation.peerId);
    if (!peer) return { text: t('notifications.directMessages', { count, shown }) };
    const name = memberName(peer);
    return { text: t('notifications.messagesFrom', { count, shown, name }), name };
  };

  const alerts = useAlertItems(siteId);

  // Idle until there is a site; a notification cannot exist without one.
  const chat = siteId ? notifications.map((n) => commsUnreadItem(siteId, n, wordCommsUnread(n))) : [];
  const items = [...chat, ...alerts.items].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  return { items, count: chat.length + alerts.unread };
}
